import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  ArrowRight,
  Instagram,
  Facebook,
  ShoppingBag,
  Zap,
  BarChart3,
  Star,
  Gift,
  CheckCircle,
  Camera,
  Share2,
  MousePointerClick,
} from "lucide-react";
import eatzyLogo from "@/assets/eatzy-teal-disc-1024.png";

const steps = [
  {
    icon: Camera,
    step: "1",
    title: "Snap your dish",
    description:
      "Open QuickPost, take a photo (or pick from your gallery), and add a caption — takes 30 seconds.",
  },
  {
    icon: Share2,
    step: "2",
    title: "Post everywhere at once",
    description:
      "One tap publishes to Instagram, Facebook, and Eatzy simultaneously — each with a live ordering link embedded.",
  },
  {
    icon: MousePointerClick,
    step: "3",
    title: "Customers tap and order",
    description:
      "Your followers see the post, tap the link, and order directly — no app download, no friction.",
  },
];

const features = [
  {
    icon: Instagram,
    title: "Instagram & Facebook, built in",
    description:
      "Connect your social accounts once. Every QuickPost goes live on both platforms automatically.",
  },
  {
    icon: ShoppingBag,
    title: "Every post is a storefront",
    description:
      "Each photo gets a tap-to-order link. Customers can browse your menu and place an order without leaving Instagram.",
  },
  {
    icon: BarChart3,
    title: "See what's working",
    description:
      "Track which posts drive the most orders. Know exactly which dishes your audience responds to.",
  },
  {
    icon: Star,
    title: "Reviews that come to you",
    description:
      "Customers who order leave reviews directly in Eatzy. Build your reputation alongside your social presence.",
  },
  {
    icon: Gift,
    title: "Kudoz loyalty, built in",
    description:
      "Customers earn Kudoz on every order. Repeat business without you having to manage a loyalty program.",
  },
  {
    icon: Zap,
    title: "Up and running in minutes",
    description:
      "No technical setup, no third-party integrations to configure. Connect Instagram and you're live.",
  },
];

export default function OwnerLanding() {
  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <nav className="border-b bg-background/95 backdrop-blur sticky top-0 z-50">
        <div className="container mx-auto px-4 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src={eatzyLogo} alt="Eatzy" className="h-8 w-8 object-contain" />
            <span className="font-semibold text-base">Eatzy for Restaurants</span>
          </div>
          <Link href="/owner/onboarding">
            <Button size="sm">
              Apply now
              <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="container mx-auto px-4 py-16 md:py-24 text-center max-w-3xl">
        <div className="inline-flex items-center gap-2 bg-primary/10 text-primary rounded-full px-4 py-1.5 mb-6 text-sm font-medium">
          <Instagram className="w-4 h-4" />
          Social Ordering for Restaurants
        </div>
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
          Turn your Instagram posts
          <br className="hidden md:block" />
          <span className="text-primary"> into direct orders</span>
        </h1>
        <p className="text-lg md:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
          One photo. Published to Instagram, Facebook, and Eatzy at once — each with
          a live order link. Your followers order without leaving their feed.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link href="/owner/onboarding">
            <Button size="lg" className="gap-2 px-8 text-base">
              Get started
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
          <Link href="/partner/login">
            <Button size="lg" variant="outline" className="gap-2 text-base">
              Partner sign in
            </Button>
          </Link>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-8 text-sm text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <CheckCircle className="w-4 h-4 text-green-500" />
            No setup fees
          </div>
          <div className="flex items-center gap-1.5">
            <CheckCircle className="w-4 h-4 text-green-500" />
            Works with your existing Instagram
          </div>
          <div className="flex items-center gap-1.5">
            <CheckCircle className="w-4 h-4 text-green-500" />
            Live in under 10 minutes
          </div>
        </div>
      </section>

      {/* Platform badges */}
      <section className="border-y bg-muted/30 py-5">
        <div className="container mx-auto px-4">
          <p className="text-center text-xs text-muted-foreground uppercase tracking-widest mb-4 font-medium">
            One post. Three platforms.
          </p>
          <div className="flex items-center justify-center gap-8 flex-wrap">
            {[
              { icon: Instagram, label: "Instagram" },
              { icon: Facebook, label: "Facebook" },
              { icon: ShoppingBag, label: "Eatzy" },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Icon className="w-5 h-5 text-foreground" />
                {label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="container mx-auto px-4 py-16 md:py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-3">How QuickPost works</h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Post once, reach everywhere, and let customers order instantly — without any extra steps on your end.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {steps.map((s) => (
            <div key={s.step} className="flex flex-col items-center text-center gap-4">
              <div className="relative">
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                  <s.icon className="w-6 h-6 text-primary" />
                </div>
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">
                  {s.step}
                </span>
              </div>
              <div>
                <h3 className="font-semibold text-base mb-1">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Features grid */}
      <section className="bg-muted/30 border-y py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-3">Everything you need</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Built for restaurants that already have a social following and want to make it work harder.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {features.map((f) => (
              <Card key={f.title}>
                <CardContent className="p-5">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <f.icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-1.5">{f.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{f.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container mx-auto px-4 py-16 md:py-24 text-center max-w-2xl">
        <h2 className="text-3xl md:text-4xl font-bold mb-4">
          Ready to make your posts work for you?
        </h2>
        <p className="text-muted-foreground mb-8">
          Apply to join Eatzy. Our team reviews every application and gets you set up personally.
        </p>
        <Link href="/owner/onboarding">
          <Button size="lg" className="gap-2 px-8 text-base">
            Apply now
            <ArrowRight className="w-4 h-4" />
          </Button>
        </Link>
        <p className="text-sm text-muted-foreground mt-4">
          Already a partner?{" "}
          <Link href="/partner/login" className="text-primary hover:underline">
            Sign in to your portal
          </Link>
        </p>
      </section>

      {/* Footer */}
      <footer className="border-t py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src={eatzyLogo} alt="Eatzy" className="h-6 w-6 object-contain" />
              <span className="font-semibold text-sm">Eatzy for Restaurants</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <Link href="/privacy">Privacy Policy</Link>
              <Link href="/terms">Terms of Service</Link>
              <Link href="/login">Customer app</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
